import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { difference } from "lodash-es";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import { TravellerSlotDrawer } from "@/features/travellers/components/TravellerSlotDrawer";
import type { TravellerSlot } from "@/features/travellers/components/TravellerSlotDrawer";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/features/travellers/utils/ageValidation";
import {
  getTravelerTypeOfAge,
  travelerTypeToTypeCode,
} from "@/features/travellers/utils/transformTraveler";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/travellers",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  const { data: hotelData } = useHotelDataContext();
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const { data: booking } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const handleContinue = () => {
    if (!booking) {
      return;
    }

    const totalPrice = calculateBookingPrice(booking);

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking.offerId,
        price: totalPrice.value,
      },
    );

    navigate({ to: "/hotels/$id/summary" });
  };

  const [isTravellerSlotDrawerOpen, setIsTravellerSlotDrawerOpen] =
    useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TravellerSlot | null>(null);

  const travelDate = useMemo(
    () => (searchData ? new Date(searchData.dates.checkIn) : null),
    [searchData],
  );

  const childrenAges = useMemo(
    () => searchData?.rooms.flatMap((room) => room.childrenAges) ?? [],
    [searchData],
  );

  const selectedChildrenAges = useMemo(() => {
    if (!travelDate || !travellers) return [];

    return travellerSlots.CHD.filter(Boolean).map((id) => {
      const foundTraveller = travellers.find((t) => t.id === id);
      if (!foundTraveller) return null;

      return getTravellerAgeAtTravelDate(foundTraveller.birthDate, travelDate);
    });
  }, [travelDate, travellerSlots.CHD, travellers]);

  const requiredAges = useMemo(() => {
    if (!selectedSlot || selectedSlot.ageGroup === "ADT") return undefined;

    const remainingChildAges = difference(childrenAges, selectedChildrenAges);

    return remainingChildAges.filter(
      (age): age is number =>
        age !== null &&
        travelerTypeToTypeCode(getTravelerTypeOfAge(age)) ===
          selectedSlot.ageGroup,
    );
  }, [selectedSlot, childrenAges, selectedChildrenAges]);

  if (!searchData || !travelDate || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel travelers page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <div className="px-4">
      <h2 className="text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Enter traveller details",
          description: "Page heading for the trip travellers selection page",
        })}
      </h2>
      <p className="mb-4 text-sm text-text-tertiary">
        {intl.formatMessage({
          defaultMessage: "Tap each traveller below to enter their information",
          description:
            "Instruction to tap each traveller card to enter their information",
        })}
      </p>

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
          setIsTravellerSlotDrawerOpen(true);
        }}
      />

      <TravellerSlotDrawer
        open={isTravellerSlotDrawerOpen}
        selectedSlot={selectedSlot ?? undefined}
        travelDate={travelDate}
        isSelectionDisabled={(traveller) => {
          const travellerType = getTravellerTypeAtTravelDate(
            traveller.birthDate,
            travelDate,
          );
          if (travellerType === "adult") return false;

          const travellerAge = getTravellerAgeAtTravelDate(
            traveller.birthDate,
            travelDate,
          );
          const availableChildAges = difference(
            childrenAges,
            selectedChildrenAges,
          );
          return !availableChildAges.includes(travellerAge);
        }}
        requiredAges={requiredAges}
        onTravellerSelected={(travellerId, slotIndex) => {
          selectTraveller(slotIndex, travellerId);
        }}
        onClose={() => setIsTravellerSlotDrawerOpen(false)}
      />
      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          disabled={!allTravellersSelected}
          variant="contained"
          onClick={handleContinue}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description: "Button to proceed to the next hotel booking step",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
